import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import RobotoRegular from '../fonts/Roboto-Regular.ttf';
import RobotoBold from '../fonts/Roboto-Bold.ttf';
import RobotoItalic from '../fonts/Roboto-Italic.ttf';
import encabezadoA4 from './informes/encabezado_A4.png';
import pieHojaA4 from './informes/pie_hoja_A4.png';

// Misma fuente y criterios de formato que "Informe técnico con observaciones"
// (InformeTecnicoPDF.jsx): TTF estáticos, márgenes laterales de 2 cm alineados
// con los membretes, texto justificado, párrafo por línea y número de página
// renderizado dentro del View fijo del pie (funciona en PDFs multipágina).
Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoRegular, fontWeight: 400 },
        { src: RobotoBold, fontWeight: 700 },
        { src: RobotoItalic, fontWeight: 400, fontStyle: 'italic' },
    ],
});

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
        fontFamily: 'Roboto',
        fontSize: 10,
        color: '#333333',
        paddingTop: 84,      // deja lugar para el encabezado (imagen fija) + margen
        paddingBottom: 122,  // deja lugar para el pie de página (imagen fija) + margen
        paddingHorizontal: 57,  // margen izquierdo/derecho de 2 cm
    },
    encabezado: {
        position: 'absolute',
        top: 24,
        left: 57,
        right: 57,
    },
    encabezadoImg: {
        width: '100%',
    },
    pie: {
        position: 'absolute',
        bottom: 24,
        left: 57,
        right: 57,
    },
    pieImg: {
        width: '100%',
    },
    tituloPage: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.5,
        marginBottom: 2,
    },
    subtitulo: {
        fontSize: 12,
        fontWeight: 700,
        textAlign: 'center',
        color: '#555555',
        marginBottom: 14,
    },
    fila: {
        marginBottom: 4,
    },
    filaLabel: {
        fontWeight: 700,
    },
    separador: {
        borderBottomWidth: 1,
        borderBottomColor: '#d1d5db',
        marginTop: 10,
        marginBottom: 8,
    },
    seccionTitulo: {
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 12,
    },
    listaItem: {
        marginBottom: 2,
    },
    listaItemBold: {
        fontWeight: 700,
    },
    textoItalico: {
        fontStyle: 'italic',
        color: '#888888',
    },
    textoPre: {
        marginBottom: 10,
        textAlign: 'justify',
    },
    obsTitulo: {
        fontSize: 16,
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: 12,
        marginTop: 10,
    },
    obsIntro: {
        marginBottom: 6,
        textAlign: 'justify',
    },
    fecha: {
        textAlign: 'right',
        marginTop: 16,
        fontSize: 9.5,
        color: '#666666',
    },
    numeroPagina: {
        textAlign: 'center',
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
});

// "del Municipio de X" / "de la ONG X" / "de <otro>" / "de la entidad solicitante"
const organismoSolicitanteFrase = (organismo) => {
    if (!organismo) return 'de la entidad solicitante';
    if (typeof organismo === 'string' && organismo.startsWith('Municipio de')) return `del ${organismo}`;
    if (typeof organismo === 'string' && organismo.startsWith('ONG')) return `de la ${organismo}`;
    return `de ${organismo}`;
};

// Informe técnico accesible desde dentro de la Obra (previewViabilidadTécnica).
// Se arma como TEXTO (react-pdf): flujo y salto de página nativos, nunca corta
// una línea por la mitad a diferencia del viejo slicing de html2canvas.
const InformeTecnicoViabilidadPDF = ({ datos }) => {
    const {
        nombre_obra,
        expediente_proyecto,
        partido_nombre,
        lc_21_localidad,
        barrios_rppvap_multiples_str,
        barrios_otro_str,
        barrios_renabap_multiples_str,
        organismo_solicitante,
        modalidad,
        monto_obra,
        tipo_contratacion,
        plazo_obra,
        beneficiarios,
        datos_catastrales,
        cuadro_calles,
        array_tipo_subtipo_proyecto,
        descripcion_proyecto,
        fecha_salida,
    } = datos || {};

    const hayBarrios = barrios_rppvap_multiples_str || barrios_otro_str || barrios_renabap_multiples_str;

    // Frase de organismo para la sección de evaluación con gramática correcta
    // según el valor resuelto del campo "Organismo Solicitante".
    const organismoFrase = organismoSolicitanteFrase(organismo_solicitante);

    // react-pdf 4.x no soporta '\n' dentro de <Text>: los campos que se
    // imprimen en línea se pasan a una sola línea (los multilinea van por Lineas).
    const lineaUnica = (valor) => (valor || '').replace(/\s*\n+\s*/g, ' ').trim();
    const datosCatastralesLinea = lineaUnica(datos_catastrales);
    const cuadroCallesLinea = lineaUnica(cuadro_calles);

    // Solo se enumeran ítems con contenido; los guiones se ponen entre las
    // partes presentes para evitar la línea ":  -  - " cuando no hay nada que
    // enumerar (ej: ítems vacíos).
    const itemsTipoSubtipo = (array_tipo_subtipo_proyecto || [])
        .map(item => ({
            ...item,
            resto: [item?.subtipo, item?.cantidad, item?.unidad]
                .filter(v => v != null && String(v).trim() !== ''),
        }))
        .filter(item => (item?.tipo != null && String(item.tipo).trim() !== '') || item.resto.length > 0);

    // NOTA: react-pdf 4.x tiene un bug con caracteres '\n' dentro de un <Text>
    // ("Cannot read properties of undefined (reading 'unitsPerEm')"), por eso el
    // texto pre-wrap se renderiza como UNA línea por <Text> (con '\u00A0' para
    // conservar las líneas vacías) y nunca se inyecta un salto de línea literal.
    const Lineas = ({ texto, style }) => (texto || '')
        .replace(/\n{2,}/g, '\n')
        .replace(/^\n+|\n+$/g, '')
        .split('\n')
        .map((linea, i) => (
            <Text key={i} style={[style, { marginBottom: 2 }]}>
                {linea || '\u00A0'}
            </Text>
        ));

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.encabezado}>
                    <Image style={styles.encabezadoImg} src={encabezadoA4} />
                </View>

                <Text style={styles.tituloPage}>Informe técnico</Text>
                <Text style={styles.subtitulo}>Dirección Provincial de Intervenciones Complementarias</Text>

                <View>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Nombre del Proyecto/alias: </Text>
                        {nombre_obra}
                    </Text>
                    <Text style={[styles.fila, { textAlign: 'right' }]}>
                        <Text style={styles.filaLabel}>Referente al Expediente: </Text>
                        {expediente_proyecto ? expediente_proyecto : 'No especificado'}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Partido: </Text>
                        {partido_nombre || 'No especificado'}
                        <Text style={styles.filaLabel}> - Localidad: </Text>
                        {lc_21_localidad || 'No especificado'}
                    </Text>
                    {hayBarrios ? (
                        <>
                            {barrios_rppvap_multiples_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio RPPVAP: </Text>
                                    {barrios_rppvap_multiples_str}
                                </Text>
                            )}
                            {barrios_otro_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio (Otros): </Text>
                                    {barrios_otro_str}
                                </Text>
                            )}
                            {barrios_renabap_multiples_str && (
                                <Text style={styles.fila}>
                                    <Text style={styles.filaLabel}>Barrio RENABAP: </Text>
                                    {barrios_renabap_multiples_str}
                                </Text>
                            )}
                        </>
                    ) : (
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>Barrio: </Text>
                            No especificado
                        </Text>
                    )}
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Organismo Solicitante: </Text>
                        {organismo_solicitante || 'No especificado'}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Modalidad: </Text>
                        {modalidad}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Monto de obra en $: </Text>
                        {monto_obra}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Tipo de contratación: </Text>
                        {tipo_contratacion}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Plazo de obra: </Text>
                        {plazo_obra}
                        {plazo_obra ? ' días' : ''}
                    </Text>
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>Beneficiarios: </Text>
                        {beneficiarios}
                    </Text>
                    <Text style={[styles.fila, styles.filaLabel]}>Ubicación del Proyecto</Text>
                    {datosCatastralesLinea && (
                        <Text style={styles.fila}>
                            <Text style={styles.filaLabel}>- Datos catastrales: </Text>
                            {datosCatastralesLinea}
                        </Text>
                    )}
                    <Text style={styles.fila}>
                        <Text style={styles.filaLabel}>- Cuadro de calles: </Text>
                        {cuadroCallesLinea}
                    </Text>
                </View>

                <View style={styles.separador} />

                <Text style={styles.seccionTitulo} minPresenceAhead={60}>Descripción del proyecto</Text>
                {itemsTipoSubtipo.length > 0 && (
                    <View>
                        {itemsTipoSubtipo.map((item, index) => (
                            <Text key={index} style={styles.listaItem}>
                                {item.tipo && (
                                    <Text style={styles.listaItemBold}>{item.tipo}: </Text>
                                )}
                                {item.resto.join(' - ')}
                            </Text>
                        ))}
                    </View>
                )}

                {descripcion_proyecto && descripcion_proyecto.trim().length > 0 && (
                    <View style={{ marginTop: 8 }}>
                        <Lineas texto={descripcion_proyecto} style={styles.textoPre} />
                    </View>
                )}

                <View style={styles.separador} />

                <Text style={styles.obsTitulo} minPresenceAhead={70}>
                    Evaluación y factibilidad de la propuesta
                </Text>
                <Text style={styles.obsIntro}>
                    Analizada la propuesta {organismoFrase}, la Dirección Provincial de
                    Intervenciones Complementarias procedió a verificar la documentación
                    técnica presentada por la misma. En ese sentido, se considera que el
                    proyecto solicitado resulta viable técnicamente y su presupuesto
                    económicamente razonable.
                </Text>

                <Text style={styles.fecha}>Fecha de salida: {fecha_salida}</Text>

                <View fixed style={styles.pie}>
                    <Image style={styles.pieImg} src={pieHojaA4} />
                    <Text render={({ pageNumber }) => `-${pageNumber}-`} style={styles.numeroPagina} />
                </View>
            </Page>
        </Document>
    );
};

export default InformeTecnicoViabilidadPDF;